import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import dispatches from "@stores/dispatches";
import { RouteComponentProps } from "react-router-dom";
import { AppState } from "@stores/type";
import { UsersInFacilityState } from "@stores/domain/mgr/GroupHome/userInFacility/types";
import { UnitsState } from "@stores/domain/units/types";
import { ErrorsState } from "@stores/domain/errors/types";
import AdminTemplate from "@components/templates/AdminTemplate";
import RecordBatchPrinting from "@components/organisms/mgr/common/record/RecordBatchPrinting";
import { RecordEachUserList } from "@components/organisms/mgr/common/record/RecordEachUserList";
import generateSelectFieldItems from "@utils/dataNormalizer/generateSelectFieldItems";
import { FieldItem } from "@interfaces/ui/form";
import { unsetUsersSummaryGroupHome } from "@stores/domain/mgr/GroupHome/usersSummary/actions";

type StateProps = {
  users: UsersInFacilityState["users"];
  optionalCustomInfo: UnitsState["optionalCustomInfo"];
  errorState: ErrorsState["records"]["data"];
};
type DispatchProps = {
  fetchUsers: () => Promise<void>;
  fetchErrors: () => Promise<void>;
  fetchOptionalCustomInfo: () => Promise<void>;
  unsetUsersSummary: () => void;
};
type MergeProps = StateProps &
  DispatchProps &
  RouteComponentProps & {
    unitsOptions: FieldItem[];
  };

type Props = MergeProps;

/**
 * 利用者サマリ
 */
const RecordUsersSummaryCore = (props: Props): JSX.Element => {
  React.useEffect(() => {
    props.fetchOptionalCustomInfo();
    props.fetchUsers();
    props.fetchErrors();
  }, []);

  // 一括印刷のデフォルト項目上書き指定
  const options = [
    { label: "記録を選択", value: "" },
    { label: "支援記録", value: "supportB" }
  ];

  return (
    <AdminTemplate pageName="利用者ごと">
      {/* 記録の一括印刷 */}
      <RecordBatchPrinting
        history={props.history}
        options={options}
        defaultSelected="supportB"
        optionalCustomInfo={props.optionalCustomInfo}
        isUseCustomRecord
        hasReduceDataNote
        unsetUsersSummary={props.unsetUsersSummary}
      />
      {/* 利用ごとの記録 */}
      <RecordEachUserList
        users={props.users}
        optionalCustomInfo={props.optionalCustomInfo}
        errorState={props.errorState}
        unitsOptions={props.unitsOptions}
      />
    </AdminTemplate>
  );
};

const mapStateToProps = (state: AppState): StateProps => ({
  users: state.GroupHome.userInFacility.users,
  optionalCustomInfo: state.units.optionalCustomInfo,
  errorState: state.errors.records.data
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  const { GroupHome, errorsDispatcher, unitsDispatcher } = dispatches;
  const userInFacilityDispatcher = GroupHome.userInFacilityDispatcher(dispatch);
  const errorsDispatches = errorsDispatcher(dispatch);
  const unitsDispatches = unitsDispatcher(dispatch);
  return {
    fetchUsers: (): Promise<void> => userInFacilityDispatcher.fetch(),
    fetchErrors: (): Promise<void> => errorsDispatches.records(),
    fetchOptionalCustomInfo: (): Promise<void> =>
      unitsDispatches.fetchOptionalCustomInfo(),
    unsetUsersSummary: (): void => {
      dispatch(unsetUsersSummaryGroupHome());
    }
  };
};

const mergeProps = (
  stateProps: StateProps,
  dispatchProps: DispatchProps,
  ownProps: RouteComponentProps
): MergeProps => {
  const units = !Array.isArray(stateProps.optionalCustomInfo)
    ? stateProps.optionalCustomInfo.facility_units.filter((v) => v.id)
    : [];
  const unitsOptions = [
    { label: "すべてのユニット", value: "0" },
    ...generateSelectFieldItems(units, "unit_name", "id")
  ];
  return {
    unitsOptions,
    ...dispatchProps,
    ...stateProps,
    ...ownProps
  };
};

export const RecordUsersSummary = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(RecordUsersSummaryCore);
